import React from "react";
import { History } from "history";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";

import globalActions from "actions/global";
import commonCustomErrorHandlerBuilder from "./custom_error_handler";
import sendApiRequest from "app/api_request/send_api_request";
import { testerAccountPaths } from "configuration/app_paths";
import alertErrorMessage from "./alert_error_message";
import { SendApiRequestType, ErrorData } from "types/app";

const { addAlert } = globalActions;

const authenticationErrorHandler = (history: History) => () => history.push(testerAccountPaths.signOut);

const authenticatedHeaders = (authenticationToken: string) => ({ Authorization: "Bearer " + authenticationToken });

const mapStateToProps = ({ testerAccount }: { testerAccount: { authenticationToken: string } }) => ({
  authenticationToken: testerAccount.authenticationToken,
});

const mapDispatchToProps = (dispatch: Dispatch<{ type: string }>) => bindActionCreators({ addAlert }, dispatch);

type HoCProps = {
  children: (sendApiRequest: SendApiRequestType) => React.ReactElement | null;
  authenticationToken: string;
  addAlert: (type: string, message: string) => void;
};

const AuthenticatedSendApiRequestProvider = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ children, authenticationToken, addAlert }: HoCProps) => {
  const history = useHistory();
  const location = useLocation();
  const commonCustomErrorHandler = commonCustomErrorHandlerBuilder({ history, location });

  const authenticatedSendApiRequest = ({
    headers = {},
    alert = true,
    customErrorHandler,
    ...options
  }: {
    headers?: { [key: string]: string };
    alert?: boolean;
    customErrorHandler?: (error: ErrorData) => void;
  }) =>
    sendApiRequest({
      headers: { ...authenticatedHeaders(authenticationToken), ...headers },
      customErrorHandler:
        customErrorHandler ||
        ((error: ErrorData) => {
          const message = alertErrorMessage(error);

          commonCustomErrorHandler(error);
          alert && addAlert("danger", message);
        }),
      authenticationErrorHandler: authenticationErrorHandler(history),
      ...options,
    });

  return children(authenticatedSendApiRequest);
});

const AuthenticatedRequest =
  <T extends Record<string, unknown>>(Component: React.ComponentType<T>) =>
  (props: Omit<T, "sendApiRequest">) => (
    <AuthenticatedSendApiRequestProvider>
      {(sendApiRequest: SendApiRequestType) => <Component {...(props as T)} sendApiRequest={sendApiRequest} />}
    </AuthenticatedSendApiRequestProvider>
  );

export default AuthenticatedRequest;
